import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SlideProps } from './types';
import { useSlideTimeout } from './useSlideTimeout';
import { createComponentLogger } from './logger';

// ImageSlide: Displays image and auto-completes after duration
const ImageSlide: React.FC<SlideProps> = ({ source, isActive, defaultDuration, onComplete, onError }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Create logger instance once
  const log = useMemo(() => createComponentLogger('ImageSlide'), []);

  // Log component lifecycle
  useEffect(() => {
    log('🎬 ImageSlide mounted', {
      isActive,
      sourceUrl: source.url,
      defaultDuration,
      customDuration: source.duration
    });
  }, [log, isActive, source.url, source.duration, defaultDuration]);

  // Log active state changes
  useEffect(() => {
    if (isActive) {
      log('▶️  ImageSlide activated', { url: source.url });
    } else {
      log('⏸️  ImageSlide deactivated');
    }
  }, [isActive, log, source.url]);

  // Log loaded state changes
  useEffect(() => {
    log('📊 Image load state changed', { isLoaded });
  }, [isLoaded, log]);

  // Use the shared timeout hook
  useSlideTimeout({
    isActive,
    isContentReady: isLoaded,
    contentDuration: source.duration,
    defaultDuration,
    onComplete,
    componentName: 'ImageSlide',
    log,
  });

  const handleLoad = () => {
    log('✅ Image loaded successfully', {
      url: source.url,
      naturalWidth: imgRef.current?.naturalWidth,
      naturalHeight: imgRef.current?.naturalHeight
    });
    setIsLoaded(true);
  };

  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    log('❌ Image failed to load', {
      url: source.url,
      error: e.type
    });
    setIsLoaded(false);
    onError(e);
  };

  return (
    <img
      ref={imgRef}
      data-src={source.url}
      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
      onLoad={handleLoad}
      onError={handleError}
    />
  );
};

export default ImageSlide;
